import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    postExcelPublicacionesModerador,
    putExcelPublicacionesTipoYEnvioApi,
    postMasivaImagenesExcelApi
} from '../../../../api/axios';
import './CreacionMasivaWizard.css';
import './ActualizacionMasivaWizard.css';

const KIND_PRECIO = 'precio';
const KIND_TIPO = 'tipo';
const KIND_IMAGENES = 'imagenes';

/** Misma secuencia que CreacionMasivaWizard */
const STEP_LABELS = ['Contexto', 'Lote', 'Revision', 'Resultado'];
const ROWS_PER_PAGE = 25;

const createRowId = () => `u-${Date.now()}-${Math.round(Math.random() * 100000)}`;

const KIND_META = {
    [KIND_PRECIO]: {
        title: 'Precio y stock',
        subtitle: 'SKU, precio lista, mega oferta y stock.'
    },
    [KIND_TIPO]: {
        title: 'Tipo de publicación y envío',
        subtitle: 'CodProducto, tipo de publicación, envío y zonas.'
    },
    [KIND_IMAGENES]: {
        title: 'Imágenes',
        subtitle: 'IdPublicacion e Img1 a Img5 (URLs o rutas).'
    }
};

const normalizeHeader = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[\s_.-]+/g, '');

const formatApiValue = (value, fallback = '') => {
    const formatted = value == null || value === '' ? '' : String(value).trim();
    return formatted || fallback;
};

const toDecimalOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return v;
    const n = Number(String(v).trim().replace(',', '.'));
    return Number.isNaN(n) ? null : n;
};

const toIntOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.parseInt(v, 10);
    const n = Number(String(v).trim().replace(',', '.'));
    return Number.isNaN(n) ? null : Number.parseInt(n, 10);
};

const normalizeKey = (s) => String(s ?? '').trim().toUpperCase();

const createEmptyRowPrecio = (source = 'manual') => ({
    id: createRowId(),
    source,
    skuMarket: '',
    precioLista: '',
    precioPromocional: '',
    stock: '',
    errors: {}
});

const createEmptyRowImagenes = (source = 'manual') => ({
    id: createRowId(),
    source,
    idPublicacion: '',
    img1: '',
    img2: '',
    img3: '',
    img4: '',
    img5: '',
    errors: {}
});

const createEmptyRowTipo = (source = 'manual') => ({
    id: createRowId(),
    source,
    codProducto: '',
    idTipoPublicacion: '',
    envio: '',
    amba: '',
    interior: '',
    patagonia: '',
    errors: {}
});

const rowFromNormalizedCells = (_norm, kind) => {
    if (kind === KIND_PRECIO) {
        return createEmptyRowPrecio('csv');
    }
    if (kind === KIND_IMAGENES) {
        return createEmptyRowImagenes('csv');
    }
    return createEmptyRowTipo('csv');
};

const fillPrecioFromNorm = (row, obj) => ({
    ...row,
    skuMarket: String(obj.sku ?? obj.skumarket ?? obj.skumarketplace ?? '').trim(),
    precioLista: String(obj.preciolista ?? '').trim(),
    precioPromocional: String(obj.megaoferta ?? obj.preciopromocional ?? '').trim(),
    stock: String(obj.stock ?? '').trim()
});

const fillImagenesFromNorm = (row, obj) => ({
    ...row,
    idPublicacion: String(obj.idpublicacion ?? '').trim(),
    img1: String(obj.img1 ?? '').trim(),
    img2: String(obj.img2 ?? '').trim(),
    img3: String(obj.img3 ?? '').trim(),
    img4: String(obj.img4 ?? '').trim(),
    img5: String(obj.img5 ?? '').trim()
});

const fillTipoFromNorm = (row, obj) => ({
    ...row,
    codProducto: String(obj.codproducto ?? '').trim(),
    idTipoPublicacion: String(obj.idtipopublicacion ?? '').trim(),
    envio: String(obj.envio ?? '').trim(),
    amba: String(obj.amba ?? '').trim(),
    interior: String(obj.interior ?? '').trim(),
    patagonia: String(obj.patagonia ?? '').trim()
});

const parseRowFromNormObject = (norm, kind) => {
    let base = rowFromNormalizedCells(norm, kind);
    if (kind === KIND_PRECIO) return fillPrecioFromNorm(base, norm);
    if (kind === KIND_IMAGENES) return fillImagenesFromNorm(base, norm);
    return fillTipoFromNorm(base, norm);
};

const isRowBlankPrecio = (row) =>
    !String(row?.skuMarket ?? '').trim() &&
    !String(row?.precioLista ?? '').trim() &&
    !String(row?.precioPromocional ?? '').trim() &&
    String(row?.stock ?? '').trim() === '';

const isRowBlankImagenes = (row) =>
    !String(row?.idPublicacion ?? '').trim() &&
    ![row?.img1, row?.img2, row?.img3, row?.img4, row?.img5].some((x) => String(x ?? '').trim());

const isRowBlankTipo = (row) => !String(row?.codProducto ?? '').trim();

const isRowBlank = (row, kind) => {
    if (kind === KIND_PRECIO) return isRowBlankPrecio(row);
    if (kind === KIND_IMAGENES) return isRowBlankImagenes(row);
    return isRowBlankTipo(row);
};

const getRowErrorsPrecio = (row, flags = { precioLista: true, promo: true, stock: true }) => {
    const errors = {};
    const sku = String(row?.skuMarket ?? '').trim();
    if (!sku) errors.skuMarket = 'Ingresá el SKU (SKU market).';
    const pl = flags.precioLista ? toDecimalOrNull(row?.precioLista) : null;
    const pp =
        flags.promo && row?.precioPromocional !== '' && row?.precioPromocional != null
            ? toDecimalOrNull(row?.precioPromocional)
            : null;
    const st = flags.stock ? (row?.stock === '' || row?.stock == null ? null : toIntOrNull(row?.stock)) : null;
    const hasAny = pl != null || pp != null || st != null;
    if (sku && !hasAny && (flags.precioLista || flags.promo || flags.stock)) {
        errors.precioLista = 'Completá al menos un campo entre precio lista, mega oferta o stock (según lo marcado en contexto).';
    }
    if (flags.precioLista && pl != null && pl < 0) errors.precioLista = 'Precio lista inválido.';
    if (flags.promo && pp != null && pp < 0) errors.precioPromocional = 'Mega oferta inválida.';
    if (flags.precioLista && flags.promo && pl != null && pp != null && pp > pl) {
        errors.precioPromocional = 'Mega oferta no puede superar precio lista.';
    }
    if (flags.stock && st != null && st < 0) errors.stock = 'Stock inválido.';
    return errors;
};

const getRowErrorsImagenes = (row) => {
    const errors = {};
    const id = String(row?.idPublicacion ?? '').trim();
    if (!id) errors.idPublicacion = 'Ingresá IdPublicacion.';
    else if (!Number.isFinite(toIntOrNull(id)) || toIntOrNull(id) < 1) errors.idPublicacion = 'IdPublicacion debe ser un entero mayor a 0.';
    const imgs = [row?.img1, row?.img2, row?.img3, row?.img4, row?.img5].map((x) => String(x ?? '').trim()).filter(Boolean);
    if (imgs.length === 0) errors.img1 = 'Indicá al menos una imagen (Img1 a Img5).';
    return errors;
};

const getRowErrorsTipo = (row) => {
    const errors = {};
    if (!String(row?.codProducto ?? '').trim()) errors.codProducto = 'Ingresá CodProducto.';
    const tp = toIntOrNull(row?.idTipoPublicacion);
    if (row?.idTipoPublicacion === '' || row?.idTipoPublicacion == null) errors.idTipoPublicacion = 'Ingresá IdTipoPublicacion.';
    else if (tp == null || tp < 0) errors.idTipoPublicacion = 'IdTipoPublicacion inválido.';
    ['envio', 'amba', 'interior', 'patagonia'].forEach((f) => {
        const v = toIntOrNull(row?.[f]);
        if (row?.[f] === '' || row?.[f] == null) errors[f] = 'Campo obligatorio (numérico).';
        else if (v == null || v < 0) errors[f] = 'Valor numérico inválido.';
    });
    return errors;
};

const getRowErrorsForKind = (row, kind, precioFlags) => {
    if (kind === KIND_PRECIO) {
        return getRowErrorsPrecio(row, precioFlags ?? { precioLista: true, promo: true, stock: true });
    }
    if (kind === KIND_IMAGENES) return getRowErrorsImagenes(row);
    return getRowErrorsTipo(row);
};

const getLiveRowErrors = (rows, kind, precioFlags) =>
    rows.map((row) => getRowErrorsForKind(row, kind, precioFlags));

const getRowsMeta = (rowsSnapshot, kind) =>
    (Array.isArray(rowsSnapshot) ? rowsSnapshot : []).map((row, index) => ({
        rowId: row?.id ?? null,
        rowNumber: index + 1,
        keyPrecio: kind === KIND_PRECIO ? normalizeKey(row?.skuMarket) : '',
        keyImg: kind === KIND_IMAGENES ? normalizeKey(row?.idPublicacion) : '',
        keyTipo: kind === KIND_TIPO ? normalizeKey(row?.codProducto) : ''
    }));

const getErrorMatchKey = (item, kind) => {
    if (kind === KIND_PRECIO) {
        return normalizeKey(item?.skuMarket ?? item?.SkuMarket ?? item?.sku ?? item?.SKU);
    }
    if (kind === KIND_IMAGENES) {
        return normalizeKey(item?.idPublicacion ?? item?.IdPublicacion);
    }
    return normalizeKey(item?.codProducto ?? item?.CodProducto ?? item?.skuMarket);
};

const getRowLabelForKind = (row, kind) => {
    if (kind === KIND_PRECIO) return formatApiValue(row?.skuMarket, '');
    if (kind === KIND_IMAGENES) return formatApiValue(row?.idPublicacion, '');
    return formatApiValue(row?.codProducto, '');
};

const enrichUpdateApiErrors = (items, rowsSnapshot, kind, createdItems = []) => {
    const rowsMeta = getRowsMeta(rowsSnapshot, kind);
    const availableIndexes = rowsMeta.map((_, i) => i);
    const createdKeys = new Set(
        (Array.isArray(createdItems) ? createdItems : [])
            .map((it) => getErrorMatchKey(it, kind))
            .filter((k) => k !== '')
    );
    if (createdKeys.size > 0) {
        for (let i = availableIndexes.length - 1; i >= 0; i -= 1) {
            const idx = availableIndexes[i];
            const meta = rowsMeta[idx];
            const k =
                kind === KIND_PRECIO ? meta.keyPrecio : kind === KIND_IMAGENES ? meta.keyImg : meta.keyTipo;
            if (createdKeys.has(k)) availableIndexes.splice(i, 1);
        }
    }

    return (Array.isArray(items) ? items : []).map((rawItem, index) => {
        const baseItem =
            rawItem && typeof rawItem === 'object' && !Array.isArray(rawItem)
                ? { ...rawItem }
                : { message: rawItem };
        let matchedIndex = -1;
        let matchType = null;

        const explicitRow = Number.parseInt(
            String(baseItem?.rowNumber ?? baseItem?.RowNumber ?? baseItem?.fila ?? baseItem?.Fila ?? ''),
            10
        );
        if (Number.isFinite(explicitRow) && explicitRow >= 1 && explicitRow <= rowsMeta.length) {
            matchedIndex = explicitRow - 1;
            matchType = 'rowNumber';
        } else {
            const cand = getErrorMatchKey(baseItem, kind);
            if (cand !== '') {
                matchedIndex = rowsMeta.findIndex((m) => {
                    const k =
                        kind === KIND_PRECIO ? m.keyPrecio : kind === KIND_IMAGENES ? m.keyImg : m.keyTipo;
                    return k === cand;
                });
                if (matchedIndex >= 0) matchType = kind === KIND_PRECIO ? 'sku' : 'key';
            }
        }
        if (matchedIndex === -1) {
            matchedIndex = availableIndexes[index] ?? availableIndexes[0] ?? index;
            if (rowsMeta[matchedIndex]) matchType = 'inferred';
        }
        const pos = availableIndexes.indexOf(matchedIndex);
        if (pos >= 0) availableIndexes.splice(pos, 1);
        const rowMeta = rowsMeta[matchedIndex];
        return {
            ...baseItem,
            __rowId: rowMeta?.rowId ?? null,
            __rowNumber: rowMeta?.rowNumber ?? null,
            __rowKey: getRowLabelForKind(rowsSnapshot[matchedIndex], kind),
            __rowMatchType: matchType
        };
    });
};

const getResultErrorHeading = (item, index) => {
    const labelBase = item?.__rowNumber
        ? `Fila ${item.__rowNumber}${item?.__rowMatchType === 'inferred' ? ' (estimada)' : ''}`
        : `Error ${index + 1}`;
    const key = formatApiValue(item?.__rowKey, '');
    return key ? `${labelBase} · ${key}` : labelBase;
};

const buildPayloadPorCodigo = (idSeller, rows, flags) => ({
    idSeller,
    listaElementos: rows.map((item) => ({
        skuMarket: String(item.skuMarket ?? '').trim(),
        precioLista: flags.precioLista ? toDecimalOrNull(item.precioLista) : null,
        precioPromocional: flags.promo ? toDecimalOrNull(item.precioPromocional) : null,
        stock: flags.stock ? toIntOrNull(item.stock) : null
    }))
});

const buildWorkbookTipo = (rows) => {
    const data = rows.map((r) => ({
        CodProducto: String(r.codProducto ?? '').trim(),
        IdTipoPublicacion: toIntOrNull(r.idTipoPublicacion) ?? 0,
        Envio: toIntOrNull(r.envio) ?? 0,
        Amba: toIntOrNull(r.amba) ?? 0,
        Interior: toIntOrNull(r.interior) ?? 0,
        Patagonia: toIntOrNull(r.patagonia) ?? 0
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Hoja1');
    return wb;
};

const buildWorkbookImagenes = (rows) => {
    const data = rows.map((r) => ({
        IdPublicacion: toIntOrNull(r.idPublicacion) ?? String(r.idPublicacion ?? '').trim(),
        Img1: String(r.img1 ?? '').trim(),
        Img2: String(r.img2 ?? '').trim(),
        Img3: String(r.img3 ?? '').trim(),
        Img4: String(r.img4 ?? '').trim(),
        Img5: String(r.img5 ?? '').trim()
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Hoja1');
    return wb;
};

const workbookToBase64 = (wb) => XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });

const parsePasteToRows = (text, kind) => {
    const lines = String(text ?? '')
        .trim()
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length < 2) return [];
    const delim = lines[0].includes('\t') ? '\t' : ';';
    const headerCells = lines[0].split(delim).map((c) => c.trim());
    const headers = headerCells.map((h) => normalizeHeader(h));
    const out = [];
    for (let i = 1; i < lines.length; i += 1) {
        const cells = lines[i].split(delim);
        const norm = {};
        headers.forEach((h, idx) => {
            norm[h] = (cells[idx] ?? '').trim();
        });
        const row = parseRowFromNormObject(norm, kind);
        if (!isRowBlank(row, kind)) out.push(row);
    }
    return out;
};

const cloneRowsSnapshot = (list) =>
    (Array.isArray(list) ? list : []).map((row) => ({
        ...row,
        errors: { ...(row?.errors ?? {}) }
    }));

const ActualizacionMasivaWizard = ({ idSeller, userType, idUsuario, tipoUsuario }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [updateKind, setUpdateKind] = useState('');
    const [rows, setRows] = useState([]);
    const [pasteValue, setPasteValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentRowsPage, setCurrentRowsPage] = useState(1);
    const [result, setResult] = useState({ created: [], errors: [] });

    const [chStock, setChStock] = useState(true);
    const [chPrecioLista, setChPrecioLista] = useState(true);
    const [chPrecioPromo, setChPrecioPromo] = useState(true);

    const isSeller = userType?.isSeller || userType?.isSellerAdmin;
    const isAdmin = userType?.isCategory || userType?.isAdminMod;
    const canTipoEnvio = isAdmin || isSeller;
    const isSellerOrAdmin =
        userType?.isSellerAdmin || userType?.isSeller || userType?.isAdmin || userType?.isAdminMod;

    const kindOptions = useMemo(() => {
        const all = [
            { id: KIND_PRECIO, ...KIND_META[KIND_PRECIO] },
            { id: KIND_TIPO, ...KIND_META[KIND_TIPO], disabled: !canTipoEnvio },
            { id: KIND_IMAGENES, ...KIND_META[KIND_IMAGENES] }
        ];
        return all;
    }, [canTipoEnvio]);

    const meta = updateKind ? KIND_META[updateKind] : null;

    const precioFlags = useMemo(
        () => ({
            precioLista: chPrecioLista,
            promo: chPrecioPromo,
            stock: chStock
        }),
        [chPrecioLista, chPrecioPromo, chStock]
    );

    const effectivePrecioFlags = isSellerOrAdmin ? precioFlags : { precioLista: true, promo: true, stock: true };

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
        setCurrentRowsPage((p) => Math.min(p, totalPages));
    }, [rows.length]);

    const resetWizard = useCallback(() => {
        setStep(0);
        setUpdateKind('');
        setRows([]);
        setPasteValue('');
        setCurrentRowsPage(1);
        setResult({ created: [], errors: [] });
        setChStock(true);
        setChPrecioLista(true);
        setChPrecioPromo(true);
    }, []);

    const addEmptyRow = () => {
        if (!updateKind) return;
        const factories = {
            [KIND_PRECIO]: createEmptyRowPrecio,
            [KIND_IMAGENES]: createEmptyRowImagenes,
            [KIND_TIPO]: createEmptyRowTipo
        };
        setRows((prev) => [...prev, factories[updateKind]()]);
    };

    const removeRow = (rowId) => {
        setRows((prev) => prev.filter((r) => r.id !== rowId));
    };

    const handleRowChange = (rowId, field, value) => {
        setRows((prev) =>
            prev.map((row) =>
                row.id === rowId
                    ? {
                          ...row,
                          [field]: value,
                          errors: { ...row.errors, [field]: undefined, ...(field === 'img1' ? { img1: undefined } : {}) }
                      }
                    : row
            )
        );
    };

    const applyRowErrors = (rowErrors) => {
        setRows((prev) => prev.map((row, i) => ({ ...row, errors: rowErrors[i] ?? {} })));
    };

    const validateRowsStep = () => {
        if (!updateKind || rows.length === 0) return [];
        const rowErrors = getLiveRowErrors(
            rows,
            updateKind,
            updateKind === KIND_PRECIO ? effectivePrecioFlags : undefined
        );
        applyRowErrors(rowErrors);
        return rowErrors;
    };

    const prepareReview = () => {
        if (!updateKind) {
            toast.warn('Elegí un tipo de actualización.');
            return false;
        }
        if (updateKind === KIND_PRECIO && isSellerOrAdmin && !String(idSeller ?? '').trim()) {
            toast.warn('No hay seller asociado para enviar el lote.');
            return false;
        }
        if (
            updateKind === KIND_PRECIO &&
            isSellerOrAdmin &&
            !chPrecioLista &&
            !chPrecioPromo &&
            !chStock
        ) {
            toast.warn('Marcá al menos un campo a actualizar: precio lista, mega oferta o stock.');
            return false;
        }
        if (rows.length === 0) {
            toast.warn('Agregá al menos una fila al lote.');
            return false;
        }
        const rowErrors = validateRowsStep();
        const hasErr = rowErrors.some((e) => Object.keys(e).length > 0);
        if (hasErr) {
            toast.warn('Revisá las filas marcadas antes de continuar.');
            return false;
        }
        return true;
    };

    const applyPaste = () => {
        if (!pasteValue.trim() || !updateKind) {
            toast.warn('Pegá texto con encabezados y filas (separador ; o tab).');
            return;
        }
        const parsed = parsePasteToRows(pasteValue, updateKind);
        if (!parsed.length) {
            toast.warn('No se pudieron interpretar filas. Incluí la fila de encabezados.');
            return;
        }
        setRows((prev) => [...prev, ...parsed]);
        setPasteValue('');
        toast.success(`${parsed.length} fila(s) agregadas desde el portapapeles.`);
    };

    const clearRowsLote = () => {
        setRows([]);
        setPasteValue('');
        setResult({ created: [], errors: [] });
        setStep(1);
        toast.info('Se vació el lote.');
    };

    const removeCsvRows = () => {
        const remaining = rows.filter((r) => r?.source !== 'csv');
        if (remaining.length === rows.length) {
            toast.info('No hay filas agregadas por pegado para eliminar.');
            return;
        }
        setRows(remaining);
        setResult({ created: [], errors: [] });
        toast.info('Se quitaron las filas cargadas por pegado.');
    };

    const returnToRowsForCorrection = () => {
        const errorResults = Array.isArray(result?.errors) ? result.errors : [];
        const failedIds = errorResults.map((item) => item?.__rowId).filter(Boolean);
        const inferredMatch = errorResults.some((item) => item?.__rowMatchType === 'inferred');
        const createdResults = Array.isArray(result?.created) ? result.created : [];
        const canKeepOnlyFailed = failedIds.length > 0 && (!inferredMatch || createdResults.length === 0);
        const nextRows = canKeepOnlyFailed ? rows.filter((row) => failedIds.includes(row.id)) : rows;
        const firstNum = errorResults.map((item) => item?.__rowNumber).find((n) => Number.isFinite(n));
        setRows(nextRows);
        setCurrentRowsPage(firstNum ? Math.max(1, Math.ceil(firstNum / ROWS_PER_PAGE)) : 1);
        setResult({ created: [], errors: [] });
        setStep(1);
        if (canKeepOnlyFailed) {
            toast.info('Quedaron solo las filas con error para corregir y reenviar.');
        } else {
            toast.info('Volvimos al lote completo. Algunos errores pueden estar estimados por fila.');
        }
    };

    const handleNext = () => {
        if (step === 0) {
            if (!updateKind) {
                toast.warn('Elegí qué tipo de actualización vas a realizar.');
                return;
            }
            setStep(1);
            return;
        }
        if (step === 1) {
            if (!prepareReview()) return;
            setStep(2);
        }
    };

    const handleBack = () => {
        if (step === 0) return;
        setStep((s) => s - 1);
    };

    const handleSubmit = async () => {
        if (!prepareReview()) {
            setStep(1);
            return;
        }
        const rowsSnapshot = cloneRowsSnapshot(rows);
        setIsSubmitting(true);
        try {
            if (updateKind === KIND_PRECIO) {
                const payload = buildPayloadPorCodigo(idSeller, rows, effectivePrecioFlags);
                const res = await postExcelPublicacionesModerador(payload);
                const errores = res?.data?.errores ?? res?.data?.errors ?? [];
                const list = Array.isArray(errores) ? errores : [];
                const enriched = enrichUpdateApiErrors(list, rowsSnapshot, KIND_PRECIO, []);
                setResult({ created: [], errors: enriched });
                setStep(3);
                if (enriched.length) enriched.forEach((e) => toast.warn(formatApiValue(e?.message ?? e?.errorMesage ?? e, 'Error')));
                else toast.success('Publicaciones actualizadas correctamente.');
                return;
            }
            if (updateKind === KIND_TIPO) {
                const wb = buildWorkbookTipo(rows);
                const b64 = workbookToBase64(wb);
                await putExcelPublicacionesTipoYEnvioApi({ file: b64 });
                setResult({ created: [], errors: [] });
                setStep(3);
                toast.success('Tipo de publicación y envío actualizados.');
                return;
            }
            const wb = buildWorkbookImagenes(rows);
            const b64 = workbookToBase64(wb);
            await postMasivaImagenesExcelApi({ file: b64 });
            setResult({ created: [], errors: [] });
            setStep(3);
            toast.success('Carga de imágenes procesada.');
        } catch (err) {
            const data = err?.response?.data;
            const raw = data?.errores ?? data?.errors ?? [];
            const list = Array.isArray(raw) ? raw : [];
            const enriched = enrichUpdateApiErrors(list, rowsSnapshot, updateKind, []);
            setResult({ created: [], errors: enriched });
            setStep(3);
            if (enriched.length) {
                enriched.forEach((e) => {
                    const msg = typeof e === 'string' ? e : e?.message ?? e?.errorMesage ?? JSON.stringify(e);
                    toast.warn(msg);
                });
            } else {
                toast.error('No se pudo completar la actualización.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (tipoUsuario === 6) {
        return null;
    }

    const safeRows = rows;
    const liveRowErrors = getLiveRowErrors(
        safeRows,
        updateKind || KIND_PRECIO,
        updateKind === KIND_PRECIO ? effectivePrecioFlags : undefined
    );
    const rowsWithIssues = liveRowErrors.filter((e) => Object.keys(e).length > 0).length;
    const totalPages = Math.max(1, Math.ceil(safeRows.length / ROWS_PER_PAGE));
    const visibleRows = safeRows.slice((currentRowsPage - 1) * ROWS_PER_PAGE, currentRowsPage * ROWS_PER_PAGE);
    const errorResults = Array.isArray(result?.errors) ? result.errors : [];
    const createdResults = Array.isArray(result?.created) ? result.created : [];

    const renderLoteInputs = (row, rowIndex) => {
        const globalIndex = (currentRowsPage - 1) * ROWS_PER_PAGE + rowIndex;
        const re = liveRowErrors[globalIndex] ?? {};

        if (updateKind === KIND_PRECIO) {
            return (
                <>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.skuMarket ? 'is-invalid' : ''}`}
                            value={row.skuMarket}
                            onChange={(e) => handleRowChange(row.id, 'skuMarket', e.target.value)}
                            placeholder="Pegá el SKU market (código de la publicación en marketplace)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.precioLista ? 'is-invalid' : ''}`}
                            value={row.precioLista}
                            onChange={(e) => handleRowChange(row.id, 'precioLista', e.target.value)}
                            placeholder="Precio de lista (solo número, ej. 1999.99)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.precioPromocional ? 'is-invalid' : ''}`}
                            value={row.precioPromocional}
                            onChange={(e) => handleRowChange(row.id, 'precioPromocional', e.target.value)}
                            placeholder="Mega oferta / precio promocional (número, vacío si no aplica)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.stock ? 'is-invalid' : ''}`}
                            value={row.stock}
                            onChange={(e) => handleRowChange(row.id, 'stock', e.target.value)}
                            placeholder="Stock disponible (entero, ej. 10)"
                        />
                    </td>
                </>
            );
        }
        if (updateKind === KIND_IMAGENES) {
            const imgPlaceholders = {
                img1: 'URL o ruta de la imagen principal (Img1)',
                img2: 'URL o ruta Img2 (opcional)',
                img3: 'URL o ruta Img3 (opcional)',
                img4: 'URL o ruta Img4 (opcional)',
                img5: 'URL o ruta Img5 (opcional)'
            };
            return (
                <>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.idPublicacion ? 'is-invalid' : ''}`}
                            value={row.idPublicacion}
                            onChange={(e) => handleRowChange(row.id, 'idPublicacion', e.target.value)}
                            placeholder="ID numérico de la publicación a actualizar"
                        />
                    </td>
                    {['img1', 'img2', 'img3', 'img4', 'img5'].map((f) => (
                        <td key={f}>
                            <input
                                className={`form-control form-control-sm ${re[f] ? 'is-invalid' : ''}`}
                                value={row[f]}
                                onChange={(e) => handleRowChange(row.id, f, e.target.value)}
                                placeholder={imgPlaceholders[f]}
                            />
                        </td>
                    ))}
                </>
            );
        }
        return (
            <>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.codProducto ? 'is-invalid' : ''}`}
                        value={row.codProducto}
                        onChange={(e) => handleRowChange(row.id, 'codProducto', e.target.value)}
                        placeholder="Código de producto (CodProducto del Excel)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.idTipoPublicacion ? 'is-invalid' : ''}`}
                        value={row.idTipoPublicacion}
                        onChange={(e) => handleRowChange(row.id, 'idTipoPublicacion', e.target.value)}
                        placeholder="ID tipo de publicación (número del catálogo)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.envio ? 'is-invalid' : ''}`}
                        value={row.envio}
                        onChange={(e) => handleRowChange(row.id, 'envio', e.target.value)}
                        placeholder="Envío: valor numérico según modelo (ej. 0)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.amba ? 'is-invalid' : ''}`}
                        value={row.amba}
                        onChange={(e) => handleRowChange(row.id, 'amba', e.target.value)}
                        placeholder="AMBA: valor numérico (zona / costo según plantilla)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.interior ? 'is-invalid' : ''}`}
                        value={row.interior}
                        onChange={(e) => handleRowChange(row.id, 'interior', e.target.value)}
                        placeholder="Interior: valor numérico (zona / costo según plantilla)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.patagonia ? 'is-invalid' : ''}`}
                        value={row.patagonia}
                        onChange={(e) => handleRowChange(row.id, 'patagonia', e.target.value)}
                        placeholder="Patagonia: valor numérico (zona / costo según plantilla)"
                    />
                </td>
            </>
        );
    };

    const theadPrecio = (
        <tr>
            <th>#</th>
            <th>SKU</th>
            <th>Precio lista</th>
            <th>Mega oferta</th>
            <th>Stock</th>
            <th />
        </tr>
    );
    const theadImg = (
        <tr>
            <th>#</th>
            <th>IdPublicacion</th>
            <th>Img1</th>
            <th>Img2</th>
            <th>Img3</th>
            <th>Img4</th>
            <th>Img5</th>
            <th />
        </tr>
    );
    const theadTipo = (
        <tr>
            <th>#</th>
            <th>CodProducto</th>
            <th>IdTipoPublicacion</th>
            <th>Envio</th>
            <th>Amba</th>
            <th>Interior</th>
            <th>Patagonia</th>
            <th />
        </tr>
    );

    return (
        <div className="container wizard-publicaciones-page">
            <div className="wizard-publicaciones-shell">
                <div className="wizard-publicaciones-hero">
                    <div>
                        <span className="wizard-publicaciones-kicker">Actualización masiva guiada</span>
                        <h2 className="wizard-publicaciones-title">Wizard para actualizar publicaciones por lote</h2>
                        <p className="wizard-publicaciones-description">
                            Mismo recorrido que el alta masiva: contexto, carga del lote fila a fila, revisión y resultado.
                            Cargá datos manualmente o pegá desde planilla. Los modelos Excel los descargás en la página
                            Actualización masiva. Tipo de publicación e imágenes se envían al servidor como Excel generado
                            desde tus filas validadas.
                        </p>
                    </div>
                    <div className="wizard-publicaciones-hero-actions">
                        <Link to="/Publicaciones/ActualizacionMasiva" className="btn btn-outline-light">
                            Volver a carga por Excel
                        </Link>
                        <button
                            type="button"
                            className="btn btn-light wizard-publicaciones-primary-light"
                            onClick={() => navigate('/Publicaciones')}
                        >
                            Ir a publicaciones
                        </button>
                    </div>
                </div>

                <div className="wizard-publicaciones-stepper">
                    {STEP_LABELS.map((label, index) => {
                        const stateClass =
                            index === step ? 'is-current' : index < step ? 'is-complete' : '';
                        return (
                            <div key={label} className={`wizard-publicaciones-step ${stateClass}`}>
                                <div className="wizard-publicaciones-step-node">{index + 1}</div>
                                <div className="wizard-publicaciones-step-label">{label}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="wizard-publicaciones-card">
                    {step === 0 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>1. Contexto del lote</h3>
                                    <p>Elegí el tipo de actualización. Las columnas y validaciones coinciden con el Excel oficial.</p>
                                </div>
                                <div className="wizard-publicaciones-tip">
                                    Si necesitás el formato Excel, descargalo desde la pantalla anterior (Actualización
                                    masiva) antes de entrar al wizard.
                                </div>
                            </div>
                            <div className="wizard-actualizacion-kind-grid">
                                {kindOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        disabled={opt.disabled}
                                        className={`wizard-actualizacion-kind-card ${
                                            updateKind === opt.id ? 'is-selected' : ''
                                        } ${opt.disabled ? 'is-disabled' : ''}`}
                                        onClick={() => !opt.disabled && setUpdateKind(opt.id)}
                                    >
                                        <span className="wizard-actualizacion-kind-title">{opt.title}</span>
                                        <span className="wizard-actualizacion-kind-sub">{opt.subtitle}</span>
                                        {opt.disabled && (
                                            <span className="wizard-actualizacion-kind-badge">No disponible para tu perfil</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {updateKind === KIND_PRECIO && (
                                <div className="wizard-publicaciones-panel mt-4">
                                    <h4>Opciones de actualización (vendedor / admin seller)</h4>
                                    <p className="text-muted small">
                                        Seller del lote: <strong>{idSeller ?? '—'}</strong>
                                        {idUsuario != null && (
                                            <>
                                                {' '}
                                                · Usuario: <strong>{idUsuario}</strong>
                                            </>
                                        )}
                                    </p>
                                    {isSellerOrAdmin && (
                                        <div className="wizard-publicaciones-switches">
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chPrecioLista}
                                                    onChange={(e) => setChPrecioLista(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar precio lista</span>
                                            </label>
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chPrecioPromo}
                                                    onChange={(e) => setChPrecioPromo(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar mega oferta</span>
                                            </label>
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chStock}
                                                    onChange={(e) => setChStock(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar stock</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 1 && meta && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>2. Lote</h3>
                                    <p>
                                        Completá la grilla a mano o pegá desde Excel (primera fila con encabezados, mismo
                                        esquema que el modelo de la pantalla anterior).
                                    </p>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-pastebox mb-3">
                                <label className="form-label">Pegar desde Excel (encabezado + filas, separador ; o tab)</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={pasteValue}
                                    onChange={(e) => setPasteValue(e.target.value)}
                                    placeholder={
                                        updateKind === KIND_PRECIO
                                            ? 'Primera fila: SKU;Precio Lista;Mega Oferta;Stock — debajo una fila por publicación'
                                            : updateKind === KIND_IMAGENES
                                              ? 'Primera fila: IdPublicacion;Img1;Img2;Img3;Img4;Img5 — URLs o rutas en cada columna'
                                              : 'Primera fila: CodProducto;IdTipoPublicacion;Envio;Amba;Interior;Patagonia'
                                    }
                                />
                                <div className="wizard-publicaciones-pastebox-actions">
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={applyPaste}>
                                        Agregar filas pegadas
                                    </button>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-table-toolbar">
                                <div className="wizard-publicaciones-table-meta">
                                    <strong>{safeRows.length}</strong> fila(s)
                                    <span>{rowsWithIssues} con datos a revisar</span>
                                    {safeRows.length > 0 && (
                                        <span>
                                            Página {currentRowsPage} de {totalPages}
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <button type="button" className="btn btn-sm btn-light" onClick={addEmptyRow}>
                                        Agregar fila
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={removeCsvRows}>
                                        Quitar filas pegadas
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearRowsLote}>
                                        Vaciar lote
                                    </button>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-table-pagination mb-2 d-flex gap-2 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentRowsPage === 1}
                                    onClick={() => setCurrentRowsPage((p) => Math.max(1, p - 1))}
                                >
                                    Anterior
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentRowsPage === totalPages}
                                    onClick={() => setCurrentRowsPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Siguiente
                                </button>
                            </div>
                            {safeRows.length === 0 ? (
                                <div className="wizard-publicaciones-empty-lote">
                                    <h4>El lote está vacío</h4>
                                    <p>Pegá datos desde planilla o agregá una fila manual.</p>
                                </div>
                            ) : (
                                <div className="wizard-publicaciones-table-wrapper">
                                    <table className="table table-sm table-hover align-middle wizard-publicaciones-table">
                                        <thead>
                                            {updateKind === KIND_PRECIO
                                                ? theadPrecio
                                                : updateKind === KIND_IMAGENES
                                                  ? theadImg
                                                  : theadTipo}
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((row, idx) => {
                                                const gi = (currentRowsPage - 1) * ROWS_PER_PAGE + idx;
                                                const re = liveRowErrors[gi] ?? {};
                                                const issues = Object.entries(re).filter(([, m]) => m);
                                                return (
                                                    <tr key={row.id}>
                                                        <td>
                                                            <div className="wizard-publicaciones-row-index">
                                                                <strong>{gi + 1}</strong>
                                                                <span>{row.source === 'csv' ? 'Pegado' : 'Manual'}</span>
                                                            </div>
                                                        </td>
                                                        {renderLoteInputs(row, idx)}
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger"
                                                                onClick={() => removeRow(row.id)}
                                                            >
                                                                Quitar
                                                            </button>
                                                            {issues.length > 0 && (
                                                                <div className="small text-danger mt-1">
                                                                    {issues.slice(0, 2).map(([k, m]) => (
                                                                        <div key={k}>{m}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && meta && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>3. Revisión</h3>
                                    <p>Confirmá el envío: {safeRows.length} fila(s) lista(s) para actualizar.</p>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                <ul className="mb-0">
                                    <li>
                                        <strong>Tipo:</strong> {meta.title}
                                    </li>
                                    {updateKind === KIND_PRECIO && (
                                        <li>
                                            <strong>Campos marcados en contexto:</strong>{' '}
                                            {isSellerOrAdmin
                                                ? [chPrecioLista && 'Precio lista', chPrecioPromo && 'Mega oferta', chStock && 'Stock']
                                                      .filter(Boolean)
                                                      .join(', ') || 'Ninguno'
                                                : 'Todos (perfil moderador)'}
                                        </li>
                                    )}
                                    <li>
                                        <strong>Seller (precio/stock):</strong> {idSeller ?? '—'}
                                    </li>
                                </ul>
                            </div>
                            <p className="text-muted small mt-3 mb-0">
                                Al confirmar se ejecuta la misma operación que la carga masiva por Excel en el backend.
                            </p>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>4. Resultado</h3>
                                    <p>Detalle de la última ejecución.</p>
                                </div>
                            </div>
                            <div className="row g-3">
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Actualizaciones exitosas</h4>
                                        {createdResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty mb-0">
                                                {errorResults.length === 0
                                                    ? 'Proceso finalizado. Si el backend no devuelve detalle por fila, el listado puede quedar vacío.'
                                                    : 'Revisá los errores al lado.'}
                                            </p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results">
                                                {createdResults.map((item, index) => (
                                                    <li key={index}>
                                                        <span>{formatApiValue(item?.idPublicacion ?? item?.skuMarket, 'OK')}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Filas con error</h4>
                                        {errorResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty mb-0">
                                                No se reportaron errores por fila en esta ejecución.
                                            </p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results wizard-publicaciones-results-danger">
                                                {errorResults.map((item, index) => (
                                                    <li key={`${item?.__rowId ?? index}-${index}`}>
                                                        <strong>{getResultErrorHeading(item, index)}</strong>
                                                        <span>
                                                            {formatApiValue(
                                                                item?.errorMesage ??
                                                                    item?.ErrorMesage ??
                                                                    item?.message ??
                                                                    item?.Message ??
                                                                    (typeof item === 'string' ? item : ''),
                                                                'Sin detalle'
                                                            )}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="wizard-publicaciones-footer">
                        <div className="wizard-publicaciones-footer-note">
                            {step < 3
                                ? 'Validamos cada fila antes de revisar y enviar. Carga manual o pegado desde planilla.'
                                : 'Corregí las filas con error y reenviá, o iniciá un lote nuevo.'}
                        </div>
                        <div className="wizard-publicaciones-footer-actions">
                            {step > 0 && step < 3 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleBack}
                                    disabled={isSubmitting}
                                >
                                    Volver
                                </button>
                            )}
                            {step < 2 && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleNext}
                                    disabled={isSubmitting}
                                >
                                    Continuar
                                </button>
                            )}
                            {step === 2 && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Enviando…' : 'Ejecutar actualización'}
                                </button>
                            )}
                            {step === 3 && (
                                <>
                                    {errorResults.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={returnToRowsForCorrection}
                                        >
                                            Volver al lote para corregir
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-outline-secondary" onClick={resetWizard}>
                                        Nueva actualización
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => navigate('/Publicaciones')}
                                    >
                                        Ir al listado
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

ActualizacionMasivaWizard.propTypes = {
    idSeller: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idUsuario: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tipoUsuario: PropTypes.number,
    userType: PropTypes.shape({
        isSeller: PropTypes.bool,
        isSellerAdmin: PropTypes.bool,
        isAdminMod: PropTypes.bool,
        isCategory: PropTypes.bool,
        isAdmin: PropTypes.bool
    })
};

const mapStateToProps = (state) => ({
    idSeller: state.general.idSeller,
    userType: state.general.userType,
    idUsuario: state.general.userId,
    tipoUsuario: state.general.tipoUsuario
});

export default connect(mapStateToProps)(ActualizacionMasivaWizard);
